/**
 * Where the ladders stand in a hedge maze.
 *
 * A profile says how many; this says where, drawn from the level's own seed so the
 * answer is the same on every device and every launch (architectural rule 4). The maze
 * is regenerated from that seed too, so a hand-authored ladder position could not
 * survive a seed change anyway — the hedge it leaned on would have moved.
 *
 * A ladder leans flat against the face of a standing hedge, inside the corridor, and
 * reaches the top of that hedge. It is scenery plus a rule, never a collider: the player
 * walks through it and climbs it by the test in `src/game/climbing.ts`. Solid rungs would
 * block the corridor and be climbed by the character controller's autostep whether that
 * was wanted or not.
 *
 * Pure and headless, like the rest of `src/game/`.
 */

import { mulberry32, seedFromString } from '../../utils/seededRandom';
import type { GroundPoint } from '../profile/types';
import { CELL_SIZE, HEDGE_THICKNESS } from './grid';
import type { Maze } from './generateMaze';

export type Ladder = {
  /** The centre of the ladder in plan, standing on the ground. */
  readonly base: GroundPoint;
  /** Unit vector from the ladder into the hedge it leans on: (±1, 0) or (0, ±1). */
  readonly into: GroundPoint;
  /** Ground to crest — the height of that hedge. */
  readonly height: number;
};

/** Rail to rail. Wide enough to read as climbable, narrow enough for a 2.4 m corridor. */
export const LADDER_WIDTH = 0.6;

/** How far the ladder stands off the hedge face — the thickness of its rails. */
export const LADDER_DEPTH = 0.12;

/**
 * How far apart placed ladders are kept, two cells. This is what makes twenty ladders
 * *spread* instead of landing wherever the shuffle happened to clump them; without it a
 * seeded draw will cheerfully put three in one junction and none in a whole quarter.
 */
export const MIN_LADDER_SPACING = 6;

/**
 * From the hedge's grid line out to the middle of a ladder leaning on its face. Also the
 * step back the other way that puts a player who has topped out on the middle of the
 * crest instead of in the air beside it.
 */
export const LADDER_HEDGE_OFFSET = HEDGE_THICKNESS / 2 + LADDER_DEPTH / 2;

/** One (cell, side) pair a ladder could stand at. */
type Candidate = {
  /** Index of the cell, so two ladders never share one. */
  cell: number;
  base: GroundPoint;
  into: GroundPoint;
};

/**
 * Every hedge face inside the maze that a ladder could lean on: the four sides of every
 * cell that has a hedge standing on them, in a fixed order so the shuffle below is the
 * only source of randomness.
 */
function candidatesOf(maze: Maze): Candidate[] {
  const mazeWidth = maze.columns * CELL_SIZE;
  const mazeDepth = maze.rows * CELL_SIZE;
  const xLine = (index: number) => -mazeWidth / 2 + index * CELL_SIZE;
  const zLine = (index: number) => -mazeDepth / 2 + index * CELL_SIZE;
  const standoff = LADDER_HEDGE_OFFSET;

  const candidates: Candidate[] = [];
  for (let z = 0; z < maze.rows; z += 1) {
    for (let x = 0; x < maze.columns; x += 1) {
      const cell = z * maze.columns + x;
      const centreX = xLine(x) + CELL_SIZE / 2;
      const centreZ = zLine(z) + CELL_SIZE / 2;

      if (maze.verticalHedges[z]?.[x]) {
        candidates.push({
          cell,
          base: { x: xLine(x) + standoff, z: centreZ },
          into: { x: -1, z: 0 },
        });
      }
      if (maze.verticalHedges[z]?.[x + 1]) {
        candidates.push({
          cell,
          base: { x: xLine(x + 1) - standoff, z: centreZ },
          into: { x: 1, z: 0 },
        });
      }
      if (maze.horizontalHedges[z]?.[x]) {
        candidates.push({
          cell,
          base: { x: centreX, z: zLine(z) + standoff },
          into: { x: 0, z: -1 },
        });
      }
      if (maze.horizontalHedges[z + 1]?.[x]) {
        candidates.push({
          cell,
          base: { x: centreX, z: zLine(z + 1) - standoff },
          into: { x: 0, z: 1 },
        });
      }
    }
  }
  return candidates;
}

/**
 * `count` ladders spread through the maze, or as many as it can hold — never more than
 * one per cell.
 *
 * Two greedy passes over one seeded shuffle. The first keeps ladders `MIN_LADDER_SPACING`
 * apart; the second, run only if that came up short, takes what is left in unused cells.
 * The second pass is what keeps the contract honest on a small maze — twenty ladders will
 * not fit six metres apart in a 4×4 — and a 12×12 never reaches it. Rejection sampling
 * ("keep drawing until they fit") was the alternative, and it is not something a level
 * build can do at load time: it has no bound.
 */
export function placeLadders(
  maze: Maze,
  seed: string,
  count: number,
  height: number,
): Ladder[] {
  if (count <= 0) return [];

  const candidates = candidatesOf(maze);
  // Namespaced like `generateMaze`'s 'maze:' draw, so one profile seed can feed both
  // without the ladders inheriting the layout's random stream.
  const random = mulberry32(seedFromString(`ladders:${seed}`));
  for (let i = candidates.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [candidates[i], candidates[j]] = [candidates[j]!, candidates[i]!];
  }

  const chosen: Ladder[] = [];
  const usedCells = new Set<number>();

  const take = (candidate: Candidate) => {
    usedCells.add(candidate.cell);
    chosen.push({ base: candidate.base, into: candidate.into, height });
  };

  const farEnough = (base: GroundPoint) =>
    chosen.every(
      (ladder) => Math.hypot(ladder.base.x - base.x, ladder.base.z - base.z) >= MIN_LADDER_SPACING,
    );

  for (const candidate of candidates) {
    if (chosen.length === count) break;
    if (usedCells.has(candidate.cell)) continue;
    if (!farEnough(candidate.base)) continue;
    take(candidate);
  }

  for (const candidate of candidates) {
    if (chosen.length === count) break;
    if (usedCells.has(candidate.cell)) continue;
    take(candidate);
  }

  return chosen;
}
